// ABOUTME: Service worker registration and lifecycle management for the TradesPurple PWA
// ABOUTME: Provides update notification and proper registration for both iOS and Android

import { Workbox } from 'workbox-window';
import { debug } from '@/lib/utils';

// Function to detect iOS devices
const isIOS = () => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

// Function to determine if on TradesPurple domain
const isTradesPurpleDomain = () => {
  return window.location.hostname === 'tradespurple.com' ||
    window.location.hostname === 'www.tradespurple.com' ||
    (window.location.hostname === 'localhost' && window.__APP_MODE__ === 'tradespurple');
};

// Service worker registration function
export function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    debug.warn('Service workers are not supported in this browser');
    return;
  }

  // Only register service worker for TradesPurple domain
  if (!isTradesPurpleDomain()) {
    debug.log('Not registering service worker on non-TradesPurple domain');
    return;
  }

  const swPath = '/service-worker.js';

  const wb = new Workbox(swPath, {
    scope: '/'
  });

  // Show update notification to the user
  const showUpdateNotification = () => {
    // Create a toast notification or use app's toast system
    if (window.confirm('New version available! Click OK to refresh.')) {
      wb.messageSkipWaiting();
      window.location.reload();
    }
  };

  // Special handling for iOS
  const iOSSpecificSetup = () => {
    // iOS PWA specific optimizations
    if (window.navigator.standalone === true) {
      // App is running in standalone mode on iOS
      document.documentElement.classList.add('ios-standalone');

      // Note: Removed iOS PWA link handling click listener to improve click responsiveness
      // React Router should handle internal navigation properly in standalone mode
    }
  };

  // Register the service worker and set up update notifications
  wb.addEventListener('activated', (event) => {
    // `event.isUpdate` will be true if another version was controlling the page previously
    if (event.isUpdate) {
      debug.log('Service worker updated - reloading page');
      window.location.reload();
    } else {
      debug.log('Service worker activated for the first time');
      // Special handling for iOS
      if (isIOS()) {
        iOSSpecificSetup();
      }
    }
  });

  wb.addEventListener('waiting', (event) => {
    debug.log('New service worker waiting to activate');
    showUpdateNotification();
  });

  wb.addEventListener('controlling', () => {
    debug.log('Successfully claimed clients');
    window.location.reload();
  });

  wb.addEventListener('message', (event) => {
    // Handle messages from the service worker
    debug.log('Message from service worker:', event.data);
  });

  // Register the service worker
  wb.register()
    .then((registration) => {
      debug.log('Service worker registered successfully:', registration);

      // Check for updates immediately
      registration.update();

      // Then check for updates every hour
      setInterval(() => {
        registration.update();
        debug.log('Checking for service worker updates');
      }, 60 * 60 * 1000);
    })
    .catch((error) => {
      debug.error('Service worker registration failed:', error);
    });

  // Export for potential use in other parts of the application
  return wb;
}

// Function to force a service worker update
export function updateServiceWorker() {
  navigator.serviceWorker.getRegistration().then(registration => {
    if (registration) {
      registration.update();
    }
  });
}

// Function to skip waiting and activate new service worker
export function skipWaiting(wb: Workbox) {
  wb.messageSkipWaiting();
}

// Initialize immediately if this is the TradesPurple application
if (typeof window !== 'undefined' && isTradesPurpleDomain()) {
  window.addEventListener('load', registerServiceWorker);
}

// Add iOS specific meta tags
if (typeof document !== 'undefined' && isIOS() && isTradesPurpleDomain()) {
  // Add the meta tags for iOS PWA enhancements
  const metaTags = [
    { name: 'apple-mobile-web-app-capable', content: 'yes' },
    { name: 'apple-mobile-web-app-status-bar-style', content: 'black-translucent' },
    { name: 'apple-mobile-web-app-title', content: 'TradesPurple' }
  ];

  metaTags.forEach(tag => {
    const metaTag = document.createElement('meta');
    metaTag.name = tag.name;
    metaTag.content = tag.content;
    document.head.appendChild(metaTag);
  });
}

// Type definitions for Safari and app mode
interface SafariNavigator extends Navigator {
  standalone?: boolean;
}

// Define a global type for APP_MODE
declare global {
  interface Window {
    __APP_MODE__?: string;
    wb?: Workbox;
    navigator: SafariNavigator;
  }
}
